import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material'
import { useAppDispatch } from '../../../../app/hooks'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import { Formik } from 'formik'
import { Currency } from '../../../../api/asset-holders/Currency'
import {
  type ChangeAccumulationCurrencyPayload
} from '../../../../api/schema-dtos/AccumulationCurrencyInstructionPayloadDtos'
import {
  useUpdateAccumulationCurrencyMutation
} from '../../../../api/asset-holders/accumulationCurrencyInstructionApi'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import OtpComponent from '../../../auth/components/OtpComponent'
import ButtonLoadingComponent from '../../../shared/components/ui-widgets/ButtonLoadingComponent'
import { type AssetHolderUpdateDto } from '../../../../api/schema-dtos/AssetHolderCrudDtos'
import ModeSharpIcon from '@mui/icons-material/ModeSharp'

interface ChangeAccumulationCurrencyComponentProps {
  assetHolderDetails: AssetHolderUpdateDto
  projectId: number
  currentCurrencyCode: string | null
}

const ChangeAccumulationCurrencyComponent: React.FC<ChangeAccumulationCurrencyComponentProps> = ({ assetHolderDetails, projectId, currentCurrencyCode }: ChangeAccumulationCurrencyComponentProps) => {
  const dispatch = useAppDispatch()
  const [openDialog, toggleDialog] = useState<boolean>(false)
  const [otp, setOtp] = useState<string>('')

  const defaultForm: ChangeAccumulationCurrencyPayload = {
    assetHolderId: assetHolderDetails.assetHolderId,
    projectId,
    currency: currentCurrencyCode ?? '',
    otp: ''
  }

  const [apiChangeAccumulationCurrency, changeAccumulationCurrencyApiResult] = useUpdateAccumulationCurrencyMutation()

  const handleChangeAccumulationCurrency = (payload: ChangeAccumulationCurrencyPayload): void => {
    apiChangeAccumulationCurrency(payload)
      .unwrap()
      .then(async () => {
        toggleDialog(false)
        dispatch(setNotification({ open: true, severity: 'success', message: `Accumulation currency changed to ${payload.currency}` }))
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  return (
    <>
      <Button
        variant={'outlined'}
        color={'primary'}
        size={'small'}

        onClick={() => {
          toggleDialog(true)
        }}
      >
        <ModeSharpIcon/>
      </Button>

      <Dialog
        open={openDialog}
        maxWidth={'sm'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => {
          toggleDialog(false)
        }}
        sx={{ p: 2 }}
      >
        <DialogTitle>Change Accumulation Currency</DialogTitle>

        <DialogContent>
          <Formik
            initialValues={defaultForm}
            onSubmit={(values) => {
              handleChangeAccumulationCurrency({
                ...values,
                otp
              })
            }}
          >
            {formikProps => (
              <form
                autoComplete={'off'}
                autoCorrect={'off'}
                onSubmit={formikProps.handleSubmit}
              >
                <Grid container spacing={2} sx={{ py: 2 }}>
                  {!(formikProps.isValid) && (
                    <Grid item md={12} sx={{ mt: 3 }}>
                      <ErrorComponent title={'Form validation error'} message={Object.values(formikProps.errors).join(', ')}/>
                    </Grid>
                  )}
                  <Grid item md={12} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>New Accumulation Currency</InputLabel>
                      <Select
                        {...formikProps.getFieldProps('currency')}
                        label="New Accumulation Currency"
                        onChange={async event => { await formikProps.setFieldValue('currency', event.target.value) }}
                      >
                        <MenuItem value={Currency.XBT}>BTC</MenuItem>
                        <MenuItem value={Currency.ZAR}>ZAR</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={12} xs={12} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <OtpComponent
                      assetHolderId={assetHolderDetails.assetHolderId}
                      actionName={'Change Currency'}
                      isLoading={changeAccumulationCurrencyApiResult.isLoading}
                      disabled={!formikProps.isValid}
                      authenticatorConfigured={assetHolderDetails.twoFAStatus}
                      executeAction={async (otp: string) => {
                        setOtp(otp)
                        await formikProps.submitForm()
                      }}
                    />
                    <Button
                      variant={'outlined'}
                      type="button"
                      onClick={() => { toggleDialog(false) }}
                      sx={{ ml: 2 }}
                      disabled={changeAccumulationCurrencyApiResult.isLoading}
                    >
                      Cancel
                      {(changeAccumulationCurrencyApiResult.isLoading && <ButtonLoadingComponent/>)}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ChangeAccumulationCurrencyComponent
